import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Snackbar,
  SnackbarContent,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { axiosInstance } from "common/network";

// ---------------------------------------------------------------------------
// Frontend settings catalog.
//
// Hierarchical settings are a flexible JSON blob with NO rigid columns, so the
// set of valid keys per scope is a convention, not a schema the API hands us.
// This is the portal's copy of that convention — keep it in sync with
// docs/hierarchical-settings-guide.md §7 and the worker's DefaultSettingsJson.
// Each field carries a human description shown under the control.
// ---------------------------------------------------------------------------
const MAX_ATTEMPTS_CAP = 5; // mirrors PreProcessorDefinition.MAX_ATTEMPTS_CAP

const SETTINGS_CATALOG = [
  {
    scope: "preprocessor.photo-validation",
    label: "Photo validation",
    description:
      "Pre-processing that validates the input photo (and can optimise it) before didimo generation.",
    fields: [
      {
        key: "enabled",
        type: "boolean",
        default: true,
        label: "Enabled",
        description:
          "Whether the photo-validation pre-processor runs at all for this account.",
      },
      {
        key: "photoEditing",
        type: "boolean",
        default: false,
        label: "Photo editing",
        description:
          "Enables the AI photo-edition step. When on, the input photo is optimised before generation; if the edition fails it falls back to the original photo (recorded as a request event).",
      },
      {
        key: "maxAttempts",
        type: "number",
        default: 1,
        min: 1,
        max: MAX_ATTEMPTS_CAP,
        label: "Max attempts",
        description: `How many times a failed but retryable pre-processing attempt is retried (also bounds photo-edition retries). Capped at ${MAX_ATTEMPTS_CAP}.`,
      },
    ],
  },
  {
    scope: "feature.customerportal.didimo-generation",
    label: "Didimo generation (customer portal)",
    description:
      "Whether the customer portal offers the Generate button to this account. Availability only — with it available the button still requires the account to have points.",
    fields: [],
  },
];

// admin_available is tri-state: null = inherit the global default, true = force
// available, false = hard block. Surface it as three explicit choices.
const AVAILABILITY_OPTIONS = [
  { value: "inherit", label: "Inherit global default" },
  { value: "available", label: "Available" },
  { value: "blocked", label: "Blocked" },
];
const toChoice = (adminAvailable) =>
  adminAvailable === true ? "available" : adminAvailable === false ? "blocked" : "inherit";
const fromChoice = (choice) =>
  choice === "available" ? true : choice === "blocked" ? false : null;

const scopeUrl = (accountUuid, scope) =>
  process.env.REACT_APP_API_ROOT + `v3/accounts/${accountUuid}/settings/${scope}`;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "white",
    marginTop: "28px",
    minHeight: "200px",
  },
  content: {
    padding: theme.spacing(3),
  },
  scope: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  scopeTitle: {
    fontWeight: 600,
  },
  scopeDesc: {
    color: "rgba(0,0,0,0.6)",
    marginBottom: theme.spacing(2),
  },
  gate: {
    minWidth: 260,
  },
  hint: {
    color: "rgba(0,0,0,0.6)",
    marginTop: theme.spacing(0.5),
    display: "block",
  },
  field: {
    marginTop: theme.spacing(2),
  },
  number: {
    width: 160,
  },
  fieldDesc: {
    color: "rgba(0,0,0,0.6)",
    display: "block",
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  actions: {
    justifyContent: "flex-end",
    paddingRight: 0,
    paddingBottom: 0,
  },
  empty: {
    padding: theme.spacing(3),
    color: "rgba(0,0,0,0.6)",
  },
  loading: {
    padding: theme.spacing(3),
    display: "flex",
    justifyContent: "center",
  },
  footnote: {
    color: "rgba(0,0,0,0.6)",
    display: "block",
    marginTop: theme.spacing(1),
  },
  snackSuccess: {
    backgroundColor: "#2e7d32",
  },
  snackError: {
    backgroundColor: "#c62828",
  },
}));

const ScopeSettings = ({ accountUuid, scope }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [available, setAvailable] = useState(false); // net effective availability
  const [reason, setReason] = useState("Ok");
  const [availabilityChoice, setAvailabilityChoice] = useState("inherit");
  const [allowChildOverride, setAllowChildOverride] = useState(false);
  const [form, setForm] = useState({}); // key -> bool (boolean) | string (number)
  const [saving, setSaving] = useState(false);
  const [gateSaving, setGateSaving] = useState(false);
  const [snack, setSnack] = useState(null); // { severity, message }

  const initForm = useCallback(
    (resolvedSettings) => {
      const next = {};
      scope.fields.forEach((f) => {
        const val = resolvedSettings ? resolvedSettings[f.key] : undefined;
        next[f.key] =
          f.type === "boolean"
            ? typeof val === "boolean"
              ? val
              : f.default
            : String(val != null ? val : f.default);
      });
      setForm(next);
    },
    [scope]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      // Effective (deep-merged) values + net availability, computed by the API.
      const resolved = await axiosInstance.get(scopeUrl(accountUuid, scope.scope) + "/resolved");
      setAvailable(!!resolved?.data?.available);
      setReason(resolved?.data?.reason || "Ok");
      initForm(resolved?.data?.settings || null);

      // Raw account-level row for the gate tri-state + allowChildOverride. 404 = no row yet.
      try {
        const raw = await axiosInstance.get(scopeUrl(accountUuid, scope.scope));
        setAvailabilityChoice(toChoice(raw?.data?.adminAvailable ?? null));
        setAllowChildOverride(!!raw?.data?.allowChildOverride);
      } catch (e) {
        setAvailabilityChoice("inherit");
        setAllowChildOverride(false);
      }
    } catch (err) {
      console.log("AccountSettings fetch:", err?.response?.status, err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [accountUuid, scope, initForm]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const numberValid = (f, value) => {
    const n = parseInt(value, 10);
    return !isNaN(n) && n >= (f.min ?? 1) && n <= (f.max ?? MAX_ATTEMPTS_CAP);
  };
  const formValid = scope.fields.every(
    (f) => f.type !== "number" || numberValid(f, form[f.key])
  );

  const saveValues = async () => {
    if (!formValid) return;
    setSaving(true);
    // PUT replaces the account-level settings blob, so send the full catalog —
    // this pins every value at the account level. "Reset to inherited" reverts it.
    const settings = {};
    scope.fields.forEach((f) => {
      settings[f.key] = f.type === "number" ? parseInt(form[f.key], 10) : !!form[f.key];
    });
    try {
      await axiosInstance.put(scopeUrl(accountUuid, scope.scope), { settings, allowChildOverride });
      setSnack({ severity: "success", message: "Settings saved." });
      await fetchAll();
    } catch (err) {
      console.log("AccountSettings save:", err);
      setSnack({ severity: "error", message: "Could not save settings." });
    } finally {
      setSaving(false);
    }
  };

  const resetValues = async () => {
    setSaving(true);
    try {
      // settings=null clears the account override (inherit global) but keeps the availability gate.
      await axiosInstance.put(scopeUrl(accountUuid, scope.scope), {
        settings: null,
        allowChildOverride,
      });
      setSnack({ severity: "success", message: "Reverted to inherited defaults." });
      await fetchAll();
    } catch (err) {
      console.log("AccountSettings reset:", err);
      setSnack({ severity: "error", message: "Could not reset settings." });
    } finally {
      setSaving(false);
    }
  };

  const changeAvailability = async (choice) => {
    setAvailabilityChoice(choice);
    setGateSaving(true);
    try {
      await axiosInstance.put(scopeUrl(accountUuid, scope.scope) + "/admin-availability", {
        available: fromChoice(choice),
      });
      setSnack({ severity: "success", message: "Availability updated." });
      await fetchAll();
    } catch (err) {
      console.log("AccountSettings availability:", err);
      setSnack({ severity: "error", message: "Could not update availability." });
      await fetchAll(); // resync the control to the server's truth
    } finally {
      setGateSaving(false);
    }
  };

  return (
    <div className={classes.scope}>
      <Typography variant="subtitle1" className={classes.scopeTitle}>
        {scope.label}
      </Typography>
      <Typography variant="body2" className={classes.scopeDesc}>
        {scope.description}
      </Typography>

      {loading ? (
        <div className={classes.loading}>
          <CircularProgress size={22} />
        </div>
      ) : error ? (
        <Typography className={classes.empty}>Could not load these settings.</Typography>
      ) : (
        <>
          {/* Availability gate — set this first: the values below have no effect while not available. */}
          <TextField
            select
            size="small"
            label="Availability"
            className={classes.gate}
            value={availabilityChoice}
            disabled={gateSaving}
            onChange={(e) => changeAvailability(e.target.value)}
          >
            {AVAILABILITY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" className={classes.hint}>
            Currently {available ? "available" : "not available"} for this account
            {reason && reason !== "Ok" ? ` (${reason})` : ""}. This gate controls whether the feature
            is usable at all; the values below only apply while it is available.
          </Typography>

          {/* Values. A scope can be availability-only (no catalogued keys) — then the
              gate above is the whole story and there is nothing to save. */}
          {scope.fields.length > 0 && (
            <>
              <Divider className={classes.divider} />

              {scope.fields.map((f) => (
                <div key={f.key} className={classes.field}>
                  {f.type === "boolean" ? (
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={!!form[f.key]}
                          disabled={!available}
                          onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.checked }))}
                        />
                      }
                      label={<Typography variant="body2">{f.label}</Typography>}
                    />
                  ) : (
                    <TextField
                      type="number"
                      size="small"
                      label={f.label}
                      className={classes.number}
                      value={form[f.key] ?? ""}
                      disabled={!available}
                      onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                      error={!numberValid(f, form[f.key])}
                      helperText={
                        !numberValid(f, form[f.key])
                          ? `Must be between ${f.min ?? 1} and ${f.max ?? MAX_ATTEMPTS_CAP}`
                          : " "
                      }
                      inputProps={{ min: f.min ?? 1, max: f.max ?? MAX_ATTEMPTS_CAP }}
                    />
                  )}
                  <Typography variant="caption" className={classes.fieldDesc}>
                    {f.description}
                  </Typography>
                </div>
              ))}

              <CardActions className={classes.actions}>
                <Button onClick={resetValues} disabled={saving} color="primary">
                  Reset to inherited
                </Button>
                <Button
                  onClick={saveValues}
                  disabled={!available || !formValid || saving}
                  variant="contained"
                  color="primary"
                  disableElevation
                >
                  {saving ? "Saving…" : "Save"}
                </Button>
              </CardActions>

              <Typography variant="caption" className={classes.footnote}>
                Changes apply to future didimo requests; requests already created keep the settings
                that were in force when they were submitted.
              </Typography>
            </>
          )}
        </>
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContent
          className={snack?.severity === "error" ? classes.snackError : classes.snackSuccess}
          message={snack?.message || ""}
        />
      </Snackbar>
    </div>
  );
};

ScopeSettings.propTypes = {
  accountUuid: PropTypes.string,
  scope: PropTypes.object.isRequired,
};

const AccountSettings = ({ accountUuid }) => {
  const classes = useStyles();
  if (!accountUuid) return null;
  return (
    <Card className={classes.root}>
      <CardHeader title="Settings" subheader="Account-level feature settings" />
      <Divider />
      <CardContent className={classes.content}>
        {SETTINGS_CATALOG.map((scope, i) => (
          <React.Fragment key={scope.scope}>
            {i > 0 && <Divider className={classes.divider} />}
            <ScopeSettings accountUuid={accountUuid} scope={scope} />
          </React.Fragment>
        ))}
      </CardContent>
    </Card>
  );
};

AccountSettings.propTypes = {
  accountUuid: PropTypes.string,
};

export default AccountSettings;
